/**
 * Deliveries Page
 * BuilderBel Construction Project Management System
 */

import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Package,
  Pencil,
  Plus,
  Trash2,
  Truck,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { Input, SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
// SearchableDropdown used for status/sort filters
import { DeliveryDrawer } from '../components/deliveries';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useCompleteDeliveryMutation,
  useConfirmDeliveryMutation,
  useDeleteDeliveryMutation,
  useGetDeliveriesQuery,
  useGetDeliveryStatsQuery,
  useMarkDeliveryArrivedMutation,
  useMarkDeliveryInTransitMutation,
  useReceiveDeliveryMutation,
} from '../services/deliveryApi';
import type { Delivery, DeliveryStatus } from '../types/delivery';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'receiving', label: 'Receiving' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'partial', label: 'Partial' },
];

const SORT_OPTIONS = [
  { value: 'requestedDate', label: 'Requested Date' },
  { value: 'createdAt', label: 'Created Date' },
  { value: 'title', label: 'Title' },
  { value: 'status', label: 'Status' },
  { value: 'vendorName', label: 'Vendor' },
  { value: 'deliveryNumber', label: 'Delivery #' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: DeliveryStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'confirmed':
      return 'info';
    case 'in_transit':
      return 'purple';
    case 'arrived':
    case 'receiving':
      return 'warning';
    case 'cancelled':
      return 'error';
    case 'partial':
      return 'warning';
    case 'scheduled':
    default:
      return 'neutral';
  }
};

const formatStatusLabel = (status: DeliveryStatus): string => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// handleDownloadPdf is defined inside the component to access toast


// ============================================================================
// COMPONENT
// ============================================================================

const Deliveries: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const handleDownloadPdf = useCallback(
    async (deliveryId: string, deliveryNumber: string) => {
      if (!projectId) return;
      try {
        const token = localStorage.getItem('auth_token');
        const url = `/api/projects/${projectId}/deliveries/${deliveryId}/pdf`;
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          toast.error('Failed to generate PDF');
          return;
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', `delivery-${deliveryNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<DeliveryStatus | ''>('');
  const [sortBy, setSortBy] = useState('requestedDate');
  const [page, setPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);

  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      ...(status && { status }),
      ...(search && { search }),
      sortBy,
      sortOrder: 'desc' as const,
    }),
    [page, status, search, sortBy]
  );

  const { data: listData, isLoading } = useGetDeliveriesQuery(
    { projectId: projectId || '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetDeliveryStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  // Mutations
  const [deleteDelivery] = useDeleteDeliveryMutation();
  const [confirmDelivery] = useConfirmDeliveryMutation();
  const [markInTransit] = useMarkDeliveryInTransitMutation();
  const [markArrived] = useMarkDeliveryArrivedMutation();
  const [receiveDelivery] = useReceiveDeliveryMutation();
  const [completeDelivery] = useCompleteDeliveryMutation();

  const deliveries = listData?.data?.deliveries || [];
  const pagination = listData?.data?.pagination;
  const stats = statsData?.data;

  // ---- Handlers ----

  const handleCreate = () => {
    setSelectedDelivery(null);
    setDrawerOpen(true);
  };

  const handleEdit = useCallback((delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (delivery: Delivery) => {
      if (!projectId) return;
      if (
        !window.confirm(`Delete delivery "${delivery.title}"? This cannot be undone.`)
      )
        return;
      try {
        await deleteDelivery({ projectId, deliveryId: delivery._id }).unwrap();
        toast.success('Delivery deleted');
      } catch {
        toast.error('Failed to delete delivery');
      }
    },
    [projectId, deleteDelivery, toast]
  );

  const handleConfirm = useCallback(
    async (delivery: Delivery) => {
      if (!projectId) return;
      try {
        await confirmDelivery({ projectId, deliveryId: delivery._id, data: {} }).unwrap();
        toast.success('Delivery confirmed');
      } catch {
        toast.error('Failed to confirm delivery');
      }
    },
    [projectId, confirmDelivery, toast]
  );

  const handleMarkInTransit = useCallback(
    async (delivery: Delivery) => {
      if (!projectId) return;
      try {
        await markInTransit({ projectId, deliveryId: delivery._id }).unwrap();
        toast.success('Delivery marked as in-transit');
      } catch {
        toast.error('Failed to update delivery status');
      }
    },
    [projectId, markInTransit, toast]
  );

  const handleMarkArrived = useCallback(
    async (delivery: Delivery) => {
      if (!projectId) return;
      try {
        await markArrived({ projectId, deliveryId: delivery._id, data: {} }).unwrap();
        toast.success('Delivery marked as arrived');
      } catch {
        toast.error('Failed to update delivery status');
      }
    },
    [projectId, markArrived, toast]
  );

  const handleReceive = useCallback(
    async (delivery: Delivery) => {
      if (!projectId) return;
      try {
        await receiveDelivery({ projectId, deliveryId: delivery._id, data: {} }).unwrap();
        toast.success('Delivery received');
      } catch {
        toast.error('Failed to receive delivery');
      }
    },
    [projectId, receiveDelivery, toast]
  );

  const handleComplete = useCallback(
    async (delivery: Delivery) => {
      if (!projectId) return;
      try {
        await completeDelivery({ projectId, deliveryId: delivery._id }).unwrap();
        toast.success('Delivery completed');
      } catch {
        toast.error('Failed to complete delivery');
      }
    },
    [projectId, completeDelivery, toast]
  );

  // Build action menu items per delivery
  const getActionItems = useCallback(
    (delivery: Delivery): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];

      const editableStatuses: DeliveryStatus[] = ['scheduled', 'confirmed'];
      if (editableStatuses.includes(delivery.status)) {
        items.push({
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleEdit(delivery),
        });
      }

      if (delivery.status === 'scheduled') {
        items.push({
          label: 'Confirm',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleConfirm(delivery),
        });
      }

      if (delivery.status === 'confirmed') {
        items.push({
          label: 'Mark In-Transit',
          icon: <Truck className="h-4 w-4" />,
          onClick: () => handleMarkInTransit(delivery),
        });
      }

      if (delivery.status === 'in_transit') {
        items.push({
          label: 'Mark Arrived',
          icon: <Package className="h-4 w-4" />,
          onClick: () => handleMarkArrived(delivery),
        });
      }

      if (delivery.status === 'arrived') {
        items.push({
          label: 'Receive Delivery',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleReceive(delivery),
        });
      }

      if (delivery.status === 'receiving') {
        items.push({
          label: 'Complete',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleComplete(delivery),
        });
      }

      items.push({
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPdf(delivery._id, delivery.deliveryNumber),
      });

      if (delivery.status === 'scheduled') {
        items.push({
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(delivery),
          variant: 'danger' as const,
        });
      }

      return items;
    },
    [
      handleEdit,
      handleConfirm,
      handleMarkInTransit,
      handleMarkArrived,
      handleReceive,
      handleComplete,
      handleDelete,
      handleDownloadPdf,
    ]
  );

  // ---- Stats cards ----
  const statsCards = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total',
        value: stats.total,
        icon: <Package className="h-4 w-4 text-gray-500" />,
      },
      {
        title: 'Today',
        value: stats.today,
        icon: <Clock className="h-4 w-4 text-brand-600" />,
        variant: 'brand' as const,
      },
      {
        title: 'Upcoming',
        value: stats.upcoming,
        icon: <Truck className="h-4 w-4 text-blue-500" />,
        variant: 'info' as const,
      },
      {
        title: 'With Issues',
        value: stats.withIssues,
        icon: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
        variant: 'warning' as const,
      },
    ];
  }, [stats]);

  if (!projectId) {
    return (
      <ModuleLayout
        title="All Deliveries"
        description="Manage deliveries and logistics across all projects"
        icon={<Package className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={Package}
          title="Select a project"
          description="Please select a project to view its deliveries."
        />
      </ModuleLayout>
    );
  }

  return (
    <>
      <ModuleLayout
        title="Deliveries"
        description="Track material and equipment deliveries for this project"
        icon={<Package className="h-4 w-4 text-brand-600" />}
        primaryAction={{
          label: 'Schedule Delivery',
          onClick: handleCreate,
          icon: <Plus className="h-4 w-4 mr-2" />,
        }}
        stats={statsCards}
      >
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="w-56">
            <Input
              placeholder="Search deliveries..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              label=""
              placeholder="Filter by status"
              value={status}
              onChange={(val: string) => {
                setStatus(val as DeliveryStatus | '');
                setPage(1);
              }}
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              label=""
              placeholder="Sort by"
              value={sortBy}
              onChange={(val: string) => setSortBy(val)}
              options={SORT_OPTIONS}
            />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <LoadingSpinner />
          </div>
        ) : deliveries.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No deliveries found"
            description={
              status || search
                ? 'No deliveries match your current filters. Try adjusting your search.'
                : 'Schedule, track, and manage material deliveries with real-time status updates.'
            }
            action={
              !status && !search
                ? { label: 'Schedule Delivery', onClick: handleCreate }
                : undefined
            }
          />
        ) : (
          <>
            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Delivery #</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">
                      Requested Date
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Location</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Issues</th>
                    <th className="w-10 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deliveries.map(delivery => (
                    <tr
                      key={delivery._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {delivery.deliveryNumber}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 max-w-[200px] truncate">
                          {delivery.title}
                        </div>
                        {delivery.sourceNumber && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {delivery.sourceNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-[140px] truncate">
                        {delivery.vendorName}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(delivery.requestedDate)}
                        {delivery.requestedWindow && (
                          <div className="text-xs text-gray-400">
                            {delivery.requestedWindow.startTime} –{' '}
                            {delivery.requestedWindow.endTime}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[140px] truncate">
                        {delivery.deliveryLocation}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={formatStatusLabel(delivery.status)}
                          variant={getStatusVariant(delivery.status)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {delivery.hasIssues ? (
                          <span className="inline-flex items-center space-x-1 text-xs text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full">
                            <AlertTriangle className="h-3 w-3" />
                            <span>Issues</span>
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <ActionMenu
                          id={`delivery-actions-${delivery._id}`}
                          items={getActionItems(delivery)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} deliveries
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={!pagination.hasPrev}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!pagination.hasNext}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </ModuleLayout>

      {/* Delivery Drawer */}
      <DeliveryDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedDelivery(null);
        }}
        projectId={projectId}
        delivery={selectedDelivery}
      />
    </>
  );
};

export default Deliveries;
